import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// dayjs(value, format, true)로 엄격 파싱을 하려면 이 플러그인이 필요하다.
// 없으면 세 번째 인자(strict)가 무시되어 존재하지 않는 날짜(예: 02-30)가 다음 날짜로 롤오버된다.
dayjs.extend(customParseFormat);
// 한국과 LA 담당자가 같은 시각을 같은 숫자로 보게 하려면 이 두 플러그인이 필요하다.
// 시각 입력창(MUI 피커)이 근무지 기준으로 동작하는 것도 여기에 달려 있다.
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 운영 근무지 시간대. 서버의 LOCAL_TIMEZONE 설정값과 같아야 한다.
 * 담당자가 한국에서 접속하든 LA에서 접속하든 화면의 시각과 "오늘"은 이 기준 하나로
 * 정해진다. 서버 조회와 자정 재계산도 같은 기준으로 돌기 때문이다.
 */
export const OPERATION_TIMEZONE = 'America/Los_Angeles';

/** 기록 시점·약속 시각을 화면에 적는 기본 형식. */
export const OPERATION_DATETIME_FORMAT = 'MM/DD/YYYY @HH:mm';

/**
 * 담당자가 어느 나라에서 접속하든 같은 업무일을 가리키는 "오늘".
 * 할 일 목록의 남은 일수, 상태 등록일 기본값처럼 업무일 기준이 필요한 곳에서 쓴다.
 * @returns {Date}
 */
export function getOperationToday() {
    // en-CA 로캘은 YYYY-MM-DD로 포맷하므로 자리 파싱이 단순하다.
    const ymd = new Date().toLocaleDateString('en-CA', { timeZone: OPERATION_TIMEZONE });
    const [year, month, day] = ymd.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/** ETD·약속 날짜처럼 시각 없이 날짜만 있는 값인지 가린다. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 서버가 보낸 기록 시점·약속 시각을 담당자 근무지 기준으로 읽는다.
 * 화면에 시각을 쓰기 전에는 항상 이 함수를 거쳐야 한국과 LA가 같은 숫자를 본다.
 *
 * 주의: 달력 날짜가 Date 객체로 넘어오면 진짜 시각과 구분할 수 없어 걸러내지 못한다.
 * ETD 같은 달력 날짜는 이 함수가 아니라 parseDateToLocal을 쓴다.
 * @param {string|number|Date|import('dayjs').Dayjs|null|undefined} value
 * @returns {import('dayjs').Dayjs|null} 값이 없거나 읽을 수 없으면 null
 */
export function toOperationTime(value) {
    if (value === null || value === undefined || value === '') return null;
    // 달력 날짜가 시각 자리로 흘러들어온 경우. 그대로 시간대를 옮기면 한국에서 하루가
    // 밀려 보이므로, 그 날짜의 근무지 자정으로 읽어 담당자가 고른 날짜를 지킨다.
    if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
        const calendarDay = dayjs.tz(value, OPERATION_TIMEZONE);
        return calendarDay.isValid() ? calendarDay : null;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.tz(OPERATION_TIMEZONE) : null;
}

/**
 * 코멘트 작성 시각, 이력 수정 시각처럼 화면에 보일 시각을 근무지 기준으로 적는다.
 * @param {string|number|Date|import('dayjs').Dayjs|null|undefined} value
 * @param {string} [format] dayjs 형식 문자열
 * @returns {string} 값이 없거나 읽을 수 없으면 빈 문자열(칸을 비워 둔다)
 */
export function formatOperationDateTime(value, format = OPERATION_DATETIME_FORMAT) {
    const operationTime = toOperationTime(value);
    return operationTime ? operationTime.format(format) : '';
}

/**
 * 근무지 기준 지금 시각. 오늘 울릴 알림인지, 올해가 몇 년인지처럼
 * 담당자의 업무 기준이 필요한 판정에서 브라우저 시계 대신 쓴다.
 * @returns {import('dayjs').Dayjs}
 */
export function operationNow() {
    return dayjs().tz(OPERATION_TIMEZONE);
}

/**
 * 담당자가 고른 약속 날짜와 시각을 하나의 약속 시점으로 묶는다.
 * 한국에서 14:30을 골라도 근무지 14:30으로 저장되어, 상태 문구와 LA 담당자 화면에
 * 같은 숫자가 뜬다.
 * @param {Date|import('dayjs').Dayjs|string|null|undefined} dateValue 약속 날짜(연·월·일만 사용)
 * @param {Date|import('dayjs').Dayjs|string|null|undefined} timeValue 약속 시각(시·분·초만 사용)
 * @returns {string|null} 서버에 보낼 시점 문자열, 둘 중 하나라도 못 읽으면 null
 */
export function combineOperationDateTime(dateValue, timeValue) {
    const datePart = dayjs(dateValue);
    const timePart = dayjs(timeValue);
    if (!datePart.isValid() || !timePart.isValid()) return null;

    // 입력창이 어느 시간대로 값을 돌려주든 담당자가 화면에서 읽은 숫자만 취해
    // 근무지 시간대로 다시 세운다. 그래야 입력한 시각과 보이는 시각이 같아진다.
    const wallClock =
        `${datePart.format('YYYY-MM-DD')} ` +
        `${String(timePart.hour()).padStart(2, '0')}:${String(timePart.minute()).padStart(2, '0')}:${String(
            timePart.second()
        ).padStart(2, '0')}`;
    const combined = dayjs.tz(wallClock, OPERATION_TIMEZONE);
    return combined.isValid() ? combined.toDate().toISOString() : null;
}

export function formatDateToISOString(date) {
    return date instanceof Date ? date.toISOString() : date;
}

export function formatDateToDateString(date) {
    if (!date || !(date instanceof Date)) return date;
    // 0~99년짜리 Date는 두 자리 연도로 입력된 값이다 — 그리드 표시도 연도를 두 자리로
    // 줄여 보여주므로 담당자에게는 20XX년으로 보인다. 읽히는 그대로 2000년대로 풀어
    // 보내야 화면에 정상으로 보이는 날짜가 저장에서 거부되는 모순이 없다.
    let year = date.getFullYear();
    if (year >= 0 && year < 100) {
        year += 2000;
    }
    // 서버 계약은 YYYY-MM-DD(연도 4자리) — 연도를 채우지 않으면 '11-01-01' 같은
    // 문자열이 만들어져 파싱 단계에서 거부된다.
    return `${String(year).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function parseDateToLocal(dateValue) {
    if (!dateValue) return null;
    
    // 이미 Date 객체인 경우
    if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    
    // 문자열인 경우
    if (typeof dateValue === 'string') {
        // "YYYY-MM-DD" 형식인 경우 로컬 시간대로 파싱
        const dateOnlyMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnlyMatch) {
            const [, year, month, day] = dateOnlyMatch;
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return isNaN(date.getTime()) ? null : date;
        }
        
        // 다른 형식인 경우 일반 파싱
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
    }
    
    // 숫자 타임스탬프인 경우
    if (typeof dateValue === 'number') {
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
}

/**
 * YYYY-MM-DD(또는 그 앞부분) 문자열에 일수를 더한 YYYY-MM-DD. 로컬 달력 기준.
 * @param {string|null|undefined} ymd
 * @param {number} daysToAdd
 * @returns {string|null}
 */
export function addDaysToYmd(ymd, daysToAdd) {
    if (ymd == null || ymd === '') return null;
    const s = String(ymd).split('T')[0];
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + daysToAdd);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
}

/**
 * 주어진 날짜가 운영 시간대(LA)의 오늘인지. 달력 팝업의 "오늘" 표시처럼 어디서 접속해도
 * LA 팀과 같은 날에 표시가 찍혀야 하는 곳에서 쓴다. 날짜 성분만 비교한다.
 */
export function isOperationToday(value) {
    const parsed = dayjs(value);
    if (!parsed.isValid()) return false;
    return formatDateToDateString(parsed.toDate()) === formatDateToDateString(getOperationToday());
}

/**
 * 두 달력 날짜("YYYY-MM-DD") 사이의 일수 (to - from).
 * 로컬 자정끼리 밀리초를 빼면 서머타임 전환 주에는 하루가 23·25시간이어서 D-day가
 * 하루 어긋난다. 날짜 성분만 UTC 자정으로 옮겨 빼면 항상 정확하다.
 */
export function daysBetweenYmd(fromYmd, toYmd) {
    const [fy, fm, fd] = String(fromYmd).slice(0, 10).split('-').map(Number);
    const [ty, tm, td] = String(toYmd).slice(0, 10).split('-').map(Number);
    if ([fy, fm, fd, ty, tm, td].some((n) => !Number.isFinite(n))) return NaN;
    return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000);
}

/** Date 형식 문자열(DataGrid 셀 직접 입력용, DateField와 동일한 규칙). */
export const CELL_DATE_INPUT_FORMAT = 'MM-DD-YYYY';

/** Date 객체를 편집용 "MM-DD-YYYY" 문자열로 변환. */
export function formatDateToMMDDYYYY(date) {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format(CELL_DATE_INPUT_FORMAT) : '';
}

/** 입력값에서 숫자만 추출해 최대 8자리까지 "MM-DD-YYYY" 형태로 하이픈을 재삽입한다. */
export function formatTypedDateInput(raw) {
    const digits = String(raw ?? '').replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('-');
}

/**
 * 셀 직접 입력을 Date로 파싱한다.
 * - "MMDD" (연도 없이 4자리 숫자)만 입력된 경우 연도를 referenceDate의 연도로 채운다.
 * - "MM-DD-YYYY" 8자리 숫자가 모두 입력된 경우 그대로 파싱한다.
 * - 존재하지 않는 날짜(예: 0230, 1332)는 dayjs strict 파싱에 의해 null로 처리된다.
 * @param {string} rawValue
 * @param {Date} [referenceDate] 기본값은 근무지 오늘 — 연말·연초에 한국에서 입력해도 담당자 기준 연도로 채운다.
 * @returns {Date|null}
 */
export function parseTypedDateInput(rawValue, referenceDate = getOperationToday()) {
    const digits = String(rawValue ?? '').replace(/\D/g, '').slice(0, 8);

    let candidate;
    if (digits.length === 4) {
        candidate = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${dayjs(referenceDate).format('YYYY')}`;
    } else if (digits.length === 8) {
        candidate = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
    } else {
        return null;
    }

    const parsed = dayjs(candidate, CELL_DATE_INPUT_FORMAT, true);
    if (!parsed.isValid()) return null;
    // 명시적으로 입력된 4자리 연도가 1000년 미만이면 연도 오타다 — DB DATE 컬럼이
    // 받지 못하는 값이므로 존재하지 않는 날짜와 똑같이 이전 값으로 되돌린다.
    if (parsed.year() < 1000) return null;
    return parsed.toDate();
}

export const formatToMMDDYY = (value) => {
    // 1) 숫자거나 길이가 매우 짧으면 날짜가 아님 → 그대로 리턴
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.length <= 3) return value;

    // 2) UTC 날짜 형태인지 판단 (대부분 ISO 형식)
    // 예: "2024-11-15", "2024-11-15T00:00:00Z"
    const isUTCDate = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T.*Z)?$/.test(value);

    if (!isUTCDate) return value;

    // 3) 기록 시점이 남은 값은 근무지 날짜로, ETD 같은 달력 날짜는 적힌 날짜 그대로
    //    보여준다. 두 경우를 가리는 일은 formatOperationDateTime이 맡는다.
    return formatOperationDateTime(value, 'MM/DD/YY') || value;
};
